/** React Query hooks over `catalog-api.ts`: books, authors, publishers, genres, reviews. */

import { useQuery, useMutation, useQueryClient, keepPreviousData, type QueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { AxiosError } from 'axios';
import {
  catalogApi,
  booksApi,
  authorsApi,
  publishersApi,
  genresApi,
  reviewsApi,
  referenceOptionsApi,
  OptionRow,
  Book,
  Author,
  Publisher,
  Genre,
  Review,
  CreateBookData,
  UpdateBookData,
  CreateAuthorData,
  AuthorQueryParams,
  CreatePublisherData,
  PublisherQueryParams,
  GenreQueryParams,
  CreateGenreData,
  CreateReviewData,
  BookQueryParams,
  BookLibrarianStats,
  AdvancedSearchParams,
} from '@/services/catalog-api';
import { BaseQueryParams } from '@/services/api-base';
import { TokenManager } from '@/lib/token-manager';

/**
 * Query keys for the catalog module
 */
export const catalogQueryKeys = {
  all: ['catalog'] as const,
  overview: () => [...catalogQueryKeys.all, 'overview'] as const,
  statistics: () => [...catalogQueryKeys.all, 'statistics'] as const,
  books: {
    all: ['books'] as const,
    lists: () => [...catalogQueryKeys.books.all, 'list'] as const,
    list: (filters: BookQueryParams = {}) => [...catalogQueryKeys.books.lists(), filters] as const,
    details: () => [...catalogQueryKeys.books.all, 'detail'] as const,
    detail: (id: number) => [...catalogQueryKeys.books.details(), id] as const,
    popular: () => [...catalogQueryKeys.books.all, 'popular'] as const,
    featured: () => [...catalogQueryKeys.books.all, 'featured'] as const,
    recent: () => [...catalogQueryKeys.books.all, 'recent'] as const,
    recommendations: () => [...catalogQueryKeys.books.all, 'recommendations'] as const,
    search: (params: AdvancedSearchParams) => [...catalogQueryKeys.books.all, 'search', params] as const,
    suggest: (q: string) => [...catalogQueryKeys.books.all, 'suggest', q] as const,
    reviews: (id: number, params: BaseQueryParams = {}) => [...catalogQueryKeys.books.detail(id), 'reviews', params] as const,
  },
  authors: {
    all: ['authors'] as const,
    lists: () => [...catalogQueryKeys.authors.all, 'list'] as const,
    list: (params: BaseQueryParams = {}) => [...catalogQueryKeys.authors.lists(), params] as const,
    details: () => [...catalogQueryKeys.authors.all, 'detail'] as const,
    detail: (id: number) => [...catalogQueryKeys.authors.details(), id] as const,
    books: (id: number, params: BaseQueryParams = {}) => [...catalogQueryKeys.authors.detail(id), 'books', params] as const,
    statistics: (id: number) => [...catalogQueryKeys.authors.detail(id), 'statistics'] as const,
    summary: () => [...catalogQueryKeys.authors.all, 'summary'] as const,
  },
  publishers: {
    all: ['publishers'] as const,
    lists: () => [...catalogQueryKeys.publishers.all, 'list'] as const,
    list: (params: BaseQueryParams = {}) => [...catalogQueryKeys.publishers.lists(), params] as const,
    details: () => [...catalogQueryKeys.publishers.all, 'detail'] as const,
    detail: (id: number) => [...catalogQueryKeys.publishers.details(), id] as const,
    books: (id: number, params: BaseQueryParams = {}) => [...catalogQueryKeys.publishers.detail(id), 'books', params] as const,
    statistics: (id: number) => [...catalogQueryKeys.publishers.detail(id), 'statistics'] as const,
    summary: () => [...catalogQueryKeys.publishers.all, 'summary'] as const,
  },
  genres: {
    all: ['genres'] as const,
    lists: () => [...catalogQueryKeys.genres.all, 'list'] as const,
    list: (params: BaseQueryParams = {}) => [...catalogQueryKeys.genres.lists(), params] as const,
    details: () => [...catalogQueryKeys.genres.all, 'detail'] as const,
    detail: (id: number) => [...catalogQueryKeys.genres.details(), id] as const,
    hierarchy: () => [...catalogQueryKeys.genres.all, 'hierarchy'] as const,
    books: (id: number, params: BaseQueryParams = {}) => [...catalogQueryKeys.genres.detail(id), 'books', params] as const,
    statistics: (id: number) => [...catalogQueryKeys.genres.detail(id), 'statistics'] as const,
    summary: () => [...catalogQueryKeys.genres.all, 'summary'] as const,
  },
  reviews: {
    all: ['reviews'] as const,
    lists: () => [...catalogQueryKeys.reviews.all, 'list'] as const,
    list: (params: BaseQueryParams = {}) => [...catalogQueryKeys.reviews.lists(), params] as const,
    details: () => [...catalogQueryKeys.reviews.all, 'detail'] as const,
    detail: (id: number) => [...catalogQueryKeys.reviews.details(), id] as const,
    my: () => [...catalogQueryKeys.reviews.all, 'my'] as const,
  },
} as const;

/** Toast the API's message, or the fallback. */
const handleMutationError = (error: unknown, defaultMessage: string) => {
  const axiosError = error as AxiosError;
  const errorData = axiosError.response?.data as Record<string, unknown>;
  const errorMessage = errorData?.detail || 
                      errorData?.message || 
                      defaultMessage;
  toast.error(errorMessage as string);
  console.error('Catalog mutation error:', error);
};

/** Toast, then invalidate the given keys. */
const handleMutationSuccess = (message: string, queryClient: QueryClient, keysToInvalidate: unknown[][]) => {
  toast.success(message);
  keysToInvalidate.forEach(key => queryClient.invalidateQueries({ queryKey: key }));
};

// ==================== Catalog Overview Hooks ====================

/**
 * Get catalog overview with statistics and highlights
 */
export const useCatalogOverview = () => {
  return useQuery({
    queryKey: catalogQueryKeys.overview(),
    queryFn: () => catalogApi.getOverview(),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Get catalog statistics
 */
export const useCatalogStatistics = () => {
  return useQuery({
    queryKey: catalogQueryKeys.statistics(),
    queryFn: () => catalogApi.getStatistics(),
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
};

// ==================== Books Hooks ====================

/**
 * Get paginated list of books with filtering
 */
export const useBooks = (params?: BookQueryParams) => {
  return useQuery({
    queryKey: catalogQueryKeys.books.list(params),
    queryFn: () => booksApi.getBooks(params),
    staleTime: 2 * 60 * 1000, // 2 minutes
    // Paging and filtering keep the current rows on screen until the next page lands.
    placeholderData: keepPreviousData,
  });
};

/**
 * Get specific book by ID
 */
export const useBook = (id: number) => {
  return useQuery({
    queryKey: catalogQueryKeys.books.detail(id),
    queryFn: () => booksApi.getBook(id),
    enabled: !!id,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Get popular books
 */
export const usePopularBooks = () => {
  return useQuery({
    queryKey: catalogQueryKeys.books.popular(),
    queryFn: () => booksApi.getPopularBooks(),
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
};

/**
 * Advanced book search hook
 */
export const useAdvancedBookSearch = (searchParams: AdvancedSearchParams) => {
  return useQuery({
    queryKey: catalogQueryKeys.books.search(searchParams),
    queryFn: () => booksApi.advancedSearch(searchParams),
    enabled: Object.keys(searchParams).length > 0,
    staleTime: 1 * 60 * 1000, // 1 minute
  });
};

/**
 * Get book reviews
 */
export const useBookReviews = (bookId: number, params?: BaseQueryParams, options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: catalogQueryKeys.books.reviews(bookId, params),
    queryFn: () => booksApi.getBookReviews(bookId, params),
    enabled: options?.enabled !== false && !!bookId,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
};

/**
 * Get featured books
 */
export const useFeaturedBooks = (limit: number = 5) => {
  return useQuery({
    queryKey: catalogQueryKeys.books.featured(),
    queryFn: () => booksApi.getFeaturedBooks(limit),
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
};

/**
 * Get recent books
 */
export const useRecentBooks = (days: number = 30) => {
  return useQuery({
    queryKey: catalogQueryKeys.books.recent(),
    queryFn: () => booksApi.getRecentBooks(days),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Get book recommendations for the current user
 */
export const useBookRecommendations = (limit: number = 5) => {
  return useQuery({
    queryKey: catalogQueryKeys.books.recommendations(),
    queryFn: () => booksApi.getRecommendations(limit),
    staleTime: 15 * 60 * 1000,
    enabled: TokenManager.isTokenValid(),
  });
};

/**
 * Create book mutation hook
 */
export const useCreateBook = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (bookData: CreateBookData) => booksApi.createBook(bookData),
    onSuccess: () => {
      handleMutationSuccess(
        'Book created successfully',
        queryClient,
        [
          [...catalogQueryKeys.books.lists()],
          [...catalogQueryKeys.overview()],
          [...catalogQueryKeys.statistics()],
        ]
      );
    },
    onError: (error) => handleMutationError(error, 'Failed to create book'),
  });
};

/**
 * Update book mutation hook
 */
export const useUpdateBook = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, bookData }: { id: number; bookData: UpdateBookData }) => 
      booksApi.updateBook(id, bookData),
    onSuccess: (data, variables) => {
      handleMutationSuccess(
        'Book updated successfully',
        queryClient,
        [
          [...catalogQueryKeys.books.lists()],
          [...catalogQueryKeys.books.detail(variables.id)],
          [...catalogQueryKeys.overview()],
        ]
      );
    },
    onError: (error) => handleMutationError(error, 'Failed to update book'),
  });
};

/**
 * Delete book mutation hook
 */
export const useDeleteBook = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (id: number) => booksApi.deleteBook(id),
    onSuccess: (_, id) => {
      handleMutationSuccess(
        'Book deleted successfully',
        queryClient,
        [
          [...catalogQueryKeys.books.lists()],
          [...catalogQueryKeys.overview()],
          [...catalogQueryKeys.statistics()],
        ]
      );
      // Remove the specific book from cache
      queryClient.removeQueries({ queryKey: catalogQueryKeys.books.detail(id) });
    },
    onError: (error) => handleMutationError(error, 'Failed to delete book'),
  });
};


/**
 * Get librarian-focused book statistics (total, available, unavailable, added today)
 */
export const useBookStatistics = () => {
  return useQuery({
    queryKey: [...catalogQueryKeys.books.all, 'librarian-stats'] as const,
    queryFn: async (): Promise<BookLibrarianStats> => {
      const res = await booksApi.getStatistics();
      return res as unknown as BookLibrarianStats;
    },
    staleTime: 60_000,
  });
};

// ==================== Authors Hooks ====================

/**
 * Get paginated list of authors
 */
export const useAuthors = (params?: AuthorQueryParams) => {
  return useQuery({
    queryKey: catalogQueryKeys.authors.list(params),
    queryFn: () => authorsApi.getAuthors(params),
    staleTime: 5 * 60 * 1000, // 5 minutes
    // Paging and filtering keep the current rows on screen until the next page lands.
    placeholderData: keepPreviousData,
  });
};

/**
 * Every publisher as {id, name}, for filter pickers (the options endpoint, not the
 * full serializer). Shares its cache entry with useCatalogData.
 */
export const usePublisherOptions = () => {
  return useQuery({
    queryKey: [...catalogQueryKeys.publishers.all, 'options'] as const,
    // Same queryFn (and so the same cached shape) as useCatalogData; each maps in `select`.
    queryFn: () => referenceOptionsApi.publishers({ page_size: 500 }),
    staleTime: 5 * 60 * 1000,
    select: (data) => data.results ?? [],
  });
};

/**
 * Search-box suggestions for text the caller has already debounced. Nothing is asked
 * for under 2 characters; the previous list stays up while the next one loads.
 */
export const useBookSuggestions = (q: string) => {
  const text = q.trim();
  return useQuery({
    queryKey: catalogQueryKeys.books.suggest(text),
    queryFn: () => booksApi.suggest(text),
    enabled: text.length >= 2,
    staleTime: 60 * 1000,
    placeholderData: keepPreviousData,
  });
};

/**
 * Read a free-text Books search as filters. A mutation because it is asked for on
 * demand (it spends TypeSafe tokens), never fired per keystroke.
 */
export const useInterpretSearch = () => {
  return useMutation({
    mutationFn: (q: string) => booksApi.interpretSearch(q),
  });
};

/**
 * Catalogue-wide author counts (staff only)
 */
export const useAuthorSummary = () => {
  return useQuery({
    queryKey: catalogQueryKeys.authors.summary(),
    queryFn: () => authorsApi.getAuthorSummary(),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Get specific author by ID
 */
export const useAuthor = (id: number) => {
  return useQuery({
    queryKey: catalogQueryKeys.authors.detail(id),
    queryFn: () => authorsApi.getAuthor(id),
    enabled: !!id,
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
};

/**
 * Get author's books
 */
export const useAuthorBooks = (id: number, params?: BaseQueryParams) => {
  return useQuery({
    queryKey: catalogQueryKeys.authors.books(id, params),
    queryFn: () => authorsApi.getAuthorBooks(id, params),
    enabled: !!id,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Get author statistics
 */
export const useAuthorStatistics = (id: number) => {
  return useQuery({
    queryKey: catalogQueryKeys.authors.statistics(id),
    queryFn: () => authorsApi.getAuthorStatistics(id),
    enabled: !!id,
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
};

/**
 * Create author mutation hook
 */
export const useCreateAuthor = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (authorData: CreateAuthorData) => authorsApi.createAuthor(authorData),
    onSuccess: () => {
      handleMutationSuccess(
        'Author created successfully',
        queryClient,
        [
          [...catalogQueryKeys.authors.lists()],
          [...catalogQueryKeys.authors.summary()],
          [...catalogQueryKeys.overview()],
        ]
      );
    },
    onError: (error) => handleMutationError(error, 'Failed to create author'),
  });
};

/**
 * Update author mutation hook
 */
export const useUpdateAuthor = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, authorData }: { id: number; authorData: Partial<CreateAuthorData> }) => 
      authorsApi.updateAuthor(id, authorData),
    onSuccess: (data, variables) => {
      handleMutationSuccess(
        'Author updated successfully',
        queryClient,
        [
          [...catalogQueryKeys.authors.lists()],
          [...catalogQueryKeys.authors.summary()],
          [...catalogQueryKeys.authors.detail(variables.id)],
        ]
      );
    },
    onError: (error) => handleMutationError(error, 'Failed to update author'),
  });
};

/**
 * Delete author mutation hook
 */
export const useDeleteAuthor = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => authorsApi.deleteAuthor(id),
    onSuccess: () => {
      handleMutationSuccess(
        'Author deleted',
        queryClient,
        [
          [...catalogQueryKeys.authors.lists()],
          [...catalogQueryKeys.authors.summary()],
          [...catalogQueryKeys.overview()],
        ]
      );
    },
    onError: (error) => handleMutationError(error, 'Failed to delete author'),
  });
};

// ==================== Publishers Hooks ====================

/**
 * Get paginated list of publishers
 */
export const usePublishers = (params?: PublisherQueryParams) => {
  return useQuery({
    queryKey: catalogQueryKeys.publishers.list(params),
    queryFn: () => publishersApi.getPublishers(params),
    staleTime: 10 * 60 * 1000, // 10 minutes
    // Paging and filtering keep the current rows on screen until the next page lands.
    placeholderData: keepPreviousData,
  });
};

/**
 * Catalogue-wide publisher counts (staff only)
 */
export const usePublisherSummary = () => {
  return useQuery({
    queryKey: catalogQueryKeys.publishers.summary(),
    queryFn: () => publishersApi.getPublisherSummary(),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Get publisher statistics (holdings + top authors)
 */
export const usePublisherStatistics = (id: number) => {
  return useQuery({
    queryKey: catalogQueryKeys.publishers.statistics(id),
    queryFn: () => publishersApi.getPublisherStatistics(id),
    enabled: !!id,
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
};

/**
 * Get specific publisher by ID
 */
export const usePublisher = (id: number) => {
  return useQuery({
    queryKey: catalogQueryKeys.publishers.detail(id),
    queryFn: () => publishersApi.getPublisher(id),
    enabled: !!id,
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
};

/**
 * Get publisher's books
 */
export const usePublisherBooks = (id: number, params?: BaseQueryParams) => {
  return useQuery({
    queryKey: catalogQueryKeys.publishers.books(id, params),
    queryFn: () => publishersApi.getPublisherBooks(id, params),
    enabled: !!id,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Create publisher mutation hook
 */
export const useCreatePublisher = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (publisherData: CreatePublisherData) => publishersApi.createPublisher(publisherData),
    onSuccess: () => {
      handleMutationSuccess(
        'Publisher created successfully',
        queryClient,
        [
          [...catalogQueryKeys.publishers.lists()],
          [...catalogQueryKeys.publishers.summary()],
          [...catalogQueryKeys.overview()],
        ]
      );
    },
    onError: (error) => handleMutationError(error, 'Failed to create publisher'),
  });
};

/**
 * Update publisher mutation hook
 */
export const useUpdatePublisher = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, publisherData }: { id: number; publisherData: Partial<CreatePublisherData> }) => 
      publishersApi.updatePublisher(id, publisherData),
    onSuccess: (data, variables) => {
      handleMutationSuccess(
        'Publisher updated successfully',
        queryClient,
        [
          [...catalogQueryKeys.publishers.lists()],
          [...catalogQueryKeys.publishers.summary()],
          [...catalogQueryKeys.publishers.detail(variables.id)],
        ]
      );
    },
    onError: (error) => handleMutationError(error, 'Failed to update publisher'),
  });
};

/**
 * Delete publisher mutation hook
 */
export const useDeletePublisher = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => publishersApi.deletePublisher(id),
    onSuccess: () => {
      handleMutationSuccess(
        'Publisher deleted',
        queryClient,
        [
          [...catalogQueryKeys.publishers.lists()],
          [...catalogQueryKeys.publishers.summary()],
          [...catalogQueryKeys.overview()],
        ]
      );
    },
    onError: (error) => handleMutationError(error, 'Failed to delete publisher'),
  });
};

// ==================== Genres Hooks ====================

/**
 * Get paginated list of genres
 */
export const useGenres = (params?: GenreQueryParams) => {
  return useQuery({
    queryKey: catalogQueryKeys.genres.list(params),
    queryFn: () => genresApi.getGenres(params),
    staleTime: 10 * 60 * 1000, // 10 minutes
    // Paging and filtering keep the current rows on screen until the next page lands.
    placeholderData: keepPreviousData,
  });
};

/**
 * Catalogue-wide genre counts (staff only)
 */
export const useGenreSummary = () => {
  return useQuery({
    queryKey: catalogQueryKeys.genres.summary(),
    queryFn: () => genresApi.getGenreSummary(),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Get specific genre by ID
 */
export const useGenre = (id: number) => {
  return useQuery({
    queryKey: catalogQueryKeys.genres.detail(id),
    queryFn: () => genresApi.getGenre(id),
    enabled: !!id,
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
};

/**
 * Get genre hierarchy
 */
export const useGenreHierarchy = () => {
  return useQuery({
    queryKey: catalogQueryKeys.genres.hierarchy(),
    queryFn: () => genresApi.getGenreHierarchy(),
    staleTime: 30 * 60 * 1000, // 30 minutes - hierarchy doesn't change often
  });
};

/**
 * Get genre's books
 */
export const useGenreBooks = (id: number, params?: BaseQueryParams) => {
  return useQuery({
    queryKey: catalogQueryKeys.genres.books(id, params),
    queryFn: () => genresApi.getGenreBooks(id, params),
    enabled: !!id,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Get genre statistics
 */
export const useGenreStatistics = (id: number) => {
  return useQuery({
    queryKey: catalogQueryKeys.genres.statistics(id),
    queryFn: () => genresApi.getGenreStatistics(id),
    enabled: !!id,
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
};

/**
 * Create genre mutation hook
 */
export const useCreateGenre = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (genreData: CreateGenreData) => genresApi.createGenre(genreData),
    onSuccess: () => {
      handleMutationSuccess(
        'Genre created successfully',
        queryClient,
        [
          [...catalogQueryKeys.genres.all],
          [...catalogQueryKeys.overview()],
        ]
      );
    },
    onError: (error) => handleMutationError(error, 'Failed to create genre'),
  });
};

/**
 * Update genre mutation hook
 */
export const useUpdateGenre = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, genreData }: { id: number; genreData: Partial<CreateGenreData> }) => 
      genresApi.updateGenre(id, genreData),
    onSuccess: (data, variables) => {
      handleMutationSuccess(
        'Genre updated successfully',
        queryClient,
        [
          // A parent change moves sub-genre lists and counts on other genres' pages.
          [...catalogQueryKeys.genres.all],
        ]
      );
    },
    onError: (error) => handleMutationError(error, 'Failed to update genre'),
  });
};

/**
 * Delete genre mutation hook. Sub-genres move up a level; books lose the tag.
 */
export const useDeleteGenre = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => genresApi.deleteGenre(id),
    onSuccess: () => {
      handleMutationSuccess(
        'Genre deleted',
        queryClient,
        [
          [...catalogQueryKeys.genres.all],
          [...catalogQueryKeys.books.lists()],
          [...catalogQueryKeys.overview()],
        ]
      );
    },
    onError: (error) => handleMutationError(error, 'Failed to delete genre'),
  });
};

// ==================== Reviews Hooks ====================

/**
 * Get paginated list of reviews
 */
export const useReviews = (params?: BaseQueryParams & { book?: number; rating?: number; is_approved?: boolean }) => {
  return useQuery({
    queryKey: catalogQueryKeys.reviews.list(params),
    queryFn: () => reviewsApi.getReviews(params),
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
};

/**
 * Get specific review by ID
 */
export const useReview = (id: number) => {
  return useQuery({
    queryKey: catalogQueryKeys.reviews.detail(id),
    queryFn: () => reviewsApi.getReview(id),
    enabled: !!id,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Get current user's reviews
 */
export const useMyReviews = (params?: BaseQueryParams) => {
  return useQuery({
    queryKey: catalogQueryKeys.reviews.my(),
    queryFn: () => reviewsApi.getMyReviews(params),
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
};

/**
 * Create review mutation hook
 */
export const useCreateReview = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (reviewData: CreateReviewData) => reviewsApi.createReview(reviewData),
    onSuccess: (newReview, variables) => {
      // Invalidate and refetch book reviews
      queryClient.invalidateQueries({ 
        queryKey: catalogQueryKeys.books.reviews(variables.book) 
      });
      
      // Invalidate book details to update review count
      queryClient.invalidateQueries({ 
        queryKey: catalogQueryKeys.books.detail(variables.book) 
      });
      
      toast.success('Review created successfully');
    },
    onError: (error: Error) => {
      console.error('Failed to create review:', error);
      toast.error('Failed to create review');
    },
  });
};

/**
 * Add review to book mutation hook
 */
export const useAddBookReview = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ bookId, reviewData }: { bookId: number; reviewData: CreateReviewData }) => 
      booksApi.addBookReview(bookId, reviewData),
    onSuccess: (newReview, variables) => {
      // Invalidate and refetch book reviews
      queryClient.invalidateQueries({ 
        queryKey: catalogQueryKeys.books.reviews(variables.bookId) 
      });
      
      // Invalidate book details to update review count
      queryClient.invalidateQueries({ 
        queryKey: catalogQueryKeys.books.detail(variables.bookId) 
      });
      
      toast.success('Review added successfully');
    },
    onError: (error: Error) => {
      console.error('Failed to add review:', error);
      toast.error('Failed to add review');
    },
  });
};

/**
 * Update review mutation hook
 */
export const useUpdateReview = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, reviewData }: { id: number; reviewData: Partial<CreateReviewData> }) => 
      reviewsApi.updateReview(id, reviewData),
    onSuccess: (data, variables) => {
      handleMutationSuccess(
        'Review updated successfully',
        queryClient,
        [
          [...catalogQueryKeys.reviews.lists()],
          [...catalogQueryKeys.reviews.detail(variables.id)],
          [...catalogQueryKeys.reviews.my()],
          [...catalogQueryKeys.books.detail(data.book)],
        ]
      );
    },
    onError: (error) => handleMutationError(error, 'Failed to update review'),
  });
};

/**
 * Delete review mutation hook
 */
export const useDeleteReview = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (id: number) => reviewsApi.deleteReview(id),
    onSuccess: (_, id) => {
      handleMutationSuccess(
        'Review deleted successfully',
        queryClient,
        [
          [...catalogQueryKeys.reviews.lists()],
          [...catalogQueryKeys.reviews.my()],
        ]
      );
      // Remove the specific review from cache
      queryClient.removeQueries({ queryKey: catalogQueryKeys.reviews.detail(id) });
    },
    onError: (error) => handleMutationError(error, 'Failed to delete review'),
  });
};

/**
 * Approve review mutation hook (Moderator only – mirrors CanModerateReviews)
 */
export const useApproveReview = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (id: number) => reviewsApi.approveReview(id),
    onSuccess: (data, id) => {
      handleMutationSuccess(
        'Review approved successfully',
        queryClient,
        [
          [...catalogQueryKeys.reviews.lists()],
          [...catalogQueryKeys.reviews.detail(id)],
          [...catalogQueryKeys.books.detail(data.book)],
          [...catalogQueryKeys.books.reviews(data.book)],
        ]
      );
    },
    onError: (error) => handleMutationError(error, 'Failed to approve review'),
  });
};
/**
 * Unapprove review mutation hook (Moderator only – mirrors CanModerateReviews)
 */
export const useUnapproveReview = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (id: number) => reviewsApi.unapproveReview(id),
    onSuccess: (data, id) => {
      handleMutationSuccess(
        'Review unapproved successfully',
        queryClient,
        [
          [...catalogQueryKeys.reviews.lists()],
          [...catalogQueryKeys.reviews.detail(id)],
          [...catalogQueryKeys.books.detail(data.book)],
          [...catalogQueryKeys.books.reviews(data.book)],
        ]
      );
    },
    onError: (error) => handleMutationError(error, 'Failed to unapprove review'),
  });
};

// ==================== Utility Hooks ====================

/**
 * Get book's average rating utility
 */
export const useBookRating = (book?: Book) => {
  return book?.average_rating ? parseFloat(book.average_rating) : 0;
};

/**
 * Check if book is available utility
 */
export const useIsBookAvailable = (book?: Book) => {
  // The backend annotates available_copies/availability_status; there is no is_available.
  if (typeof book?.available_copies === 'number') return book.available_copies > 0;
  return book?.availability_status === 'available' || book?.availability_status === 'limited';
};

/**
 * Format book display title utility
 */
export const useBookDisplayTitle = (book?: Book) => {
  if (!book) return '';
  return book.subtitle ? `${book.title}: ${book.subtitle}` : book.title;
};

/**
 * Get author's full name utility
 */
export const useAuthorDisplayName = (author?: Author) => {
  if (!author) return '';
  return author.full_name || `${author.first_name} ${author.last_name}`;
};

/**
 * Reference data for the book forms.
 *
 * Genres and publishers are bounded tables and come from the light ``options/``
 * projection (id + name, no biographies). Authors are the table that grows, so
 * only a first page is loaded and the picker searches server-side as the
 * librarian types (``useAuthorOptions``); selected authors are merged in so their
 * labels never disappear.
 */
const optionToAuthor = (o: OptionRow): Author => {
  const [first, ...rest] = o.name.split(' ');
  return { id: o.id, first_name: rest.length ? first : '', last_name: rest.length ? rest.join(' ') : o.name, full_name: o.name } as Author;
};
const optionToPublisher = (o: OptionRow): Publisher => ({ id: o.id, name: o.name } as Publisher);
const optionToGenre = (o: OptionRow): Genre => ({ id: o.id, name: o.name } as Genre);

export const useAuthorOptions = (search: string, enabled = true) => {
  const trimmed = search.trim();
  return useQuery({
    queryKey: [...catalogQueryKeys.authors.all, 'options', trimmed] as const,
    queryFn: () => referenceOptionsApi.authors({ search: trimmed || undefined, page_size: 50 }),
    enabled,
    staleTime: 60 * 1000,
    select: (data) => data.results.map(optionToAuthor),
  });
};

export const useCatalogData = (options: { authorSearch?: string } = {}) => {
  const authorsQuery = useAuthorOptions(options.authorSearch ?? '');
  const publishersQuery = useQuery({
    queryKey: [...catalogQueryKeys.publishers.all, 'options'] as const,
    queryFn: () => referenceOptionsApi.publishers({ page_size: 500 }),
    staleTime: 5 * 60 * 1000,
    select: (data) => data.results.map(optionToPublisher),
  });
  const genresQuery = useQuery({
    queryKey: [...catalogQueryKeys.genres.all, 'options'] as const,
    queryFn: () => referenceOptionsApi.genres({ page_size: 500 }),
    staleTime: 5 * 60 * 1000,
    select: (data) => data.results.map(optionToGenre),
  });

  return {
    data: {
      authors: authorsQuery.data ?? [],
      publishers: publishersQuery.data ?? [],
      genres: genresQuery.data ?? [],
    },
    isLoading: authorsQuery.isLoading || publishersQuery.isLoading || genresQuery.isLoading,
    error: authorsQuery.error || publishersQuery.error || genresQuery.error,
  };
};
